import { useEffect } from "react";
import { Link, useParams, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Play } from "lucide-react";
import { getProject, nextProject, SHOWROOM } from "@/data/projects";
import { MediaSlot, Reveal } from "@/components/work/MediaSlot";
import { Seo } from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];

function DemoAction({ project }) {
  const url = project.demoUrl || project.liveUrl;
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="project-demo-link"
        className="group flex items-center gap-2 border border-white bg-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-obsidian transition-colors duration-500 ease-cinematic hover:bg-transparent hover:text-white"
      >
        {project.demoUrl ? "View Live Demo" : "Experience the Site"}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    );
  }
  return (
    <span
      data-testid="project-demo-pending"
      className="flex items-center gap-2 border border-white/20 px-6 py-4 text-xs uppercase tracking-[0.25em] text-fog/60"
    >
      Demo in preparation
    </span>
  );
}

export default function Project() {
  const { slug } = useParams();
  const location = useLocation();

  // "Make This Yours" deep links land directly on the purchase block
  useEffect(() => {
    if (location.hash !== "#make-yours") return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("make-yours");
      if (!el) return;
      if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
      else el.scrollIntoView();
    }, 700);
    return () => clearTimeout(t);
  }, [location.hash]);

  const project = getProject(slug);
  if (!project) return <Navigate to="/work" replace />;

  const index = SHOWROOM.findIndex((p) => p.slug === slug);
  const next = nextProject(slug);
  const isTemplate = project.kind === "template";

  return (
    <div data-testid="project-page" className="pb-32 pt-32 md:pt-44">
      <Seo
        title={project.name}
        description={project.description.length > 160 ? `${project.description.slice(0, 157).trimEnd()}…` : project.description}
        path={`/work/${project.slug}`}
      />
      <div className="mx-auto max-w-[1500px] px-5 md:px-16">
        <Link
          to="/work"
          data-testid="project-back-link"
          className="flex w-fit items-center gap-2 text-xs uppercase tracking-[0.3em] text-fog transition-colors duration-300 hover:text-white"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Work
        </Link>

        {/* ── hero ─────────────────────────────────────────── */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mt-14 text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs"
        >
          {index >= 0 ? `${String(index + 1).padStart(2, "0")} — ` : ""}
          {project.category}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.08, ease: EASE }}
          className="mt-6 font-display text-5xl font-medium leading-[0.95] tracking-[-0.03em] md:text-8xl"
        >
          {project.name}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.18, ease: EASE }}
          className="mt-6 font-display text-xl leading-snug text-fog md:text-3xl"
        >
          {project.headline}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.26, ease: EASE }}
          className="mt-6 max-w-xl text-sm leading-relaxed text-fog md:text-base"
        >
          {project.description}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.34, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <DemoAction project={project} />
          {project.backendDemoUrl && (
            <a
              href={project.backendDemoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="project-backend-demo-link"
              className="group flex items-center gap-2 border border-white/25 px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] transition-colors duration-500 ease-cinematic hover:border-white hover:bg-white hover:text-obsidian"
            >
              {project.backendDemoLabel || "Owner’s Side Demo"}
              <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
          <span className="text-[10px] uppercase tracking-[0.3em] text-fog/50">
            {project.statusLabel}
          </span>
        </motion.div>

        {/* ── media sequence ───────────────────────────────── */}
        <Reveal className="mt-20 md:mt-28" delay={0}>
          <MediaSlot
            label="Project hero"
            src={project.media.hero}
            ratio="aspect-[16/9]"
            testId="media-hero"
          />
        </Reveal>

        <div className="mt-6 grid gap-6 md:grid-cols-12">
          <Reveal className="md:col-span-8" delay={0.05}>
            <MediaSlot
              label="Desktop experience"
              src={project.media.desktop}
              ratio="aspect-[16/10]"
              testId="media-desktop"
            />
          </Reveal>
          <Reveal className="md:col-span-4" delay={0.12}>
            <MediaSlot
              label="Mobile experience"
              src={project.media.mobile}
              ratio="aspect-[9/14]"
              testId="media-mobile"
            />
          </Reveal>
        </div>

        <Reveal className="mt-6" delay={0}>
          <div className="relative" data-testid="media-video-wrap">
            {project.media.video ? (
              <video
                src={project.media.video}
                controls
                preload="none"
                className="aspect-[16/9] w-full border border-white/10 object-cover"
                data-testid="media-video"
              />
            ) : (
              <div className="relative">
                <MediaSlot
                  label="Screen recording"
                  src={null}
                  ratio="aspect-[16/9]"
                  testId="media-video"
                />
                <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-obsidian/60 backdrop-blur-md">
                  <Play className="ml-0.5 h-5 w-5 text-fog" />
                </span>
              </div>
            )}
          </div>
        </Reveal>

        {/* ── two sides of the product ─────────────────────── */}
        <section className="mt-24 grid gap-6 md:mt-36 md:grid-cols-2 md:gap-10">
          <div>
            <Reveal>
              <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
                The customer&rsquo;s side
              </p>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                What customers can do.
              </h2>
              <div className="mt-8">
                <MediaSlot
                  label="Customer interaction"
                  src={project.media.customer}
                  ratio="aspect-[16/10]"
                  testId="media-customer"
                />
              </div>
            </Reveal>
          </div>
          <div>
            <Reveal delay={0.08}>
              <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
                The owner&rsquo;s side
              </p>
              <h2 className="mt-4 font-display text-3xl font-medium tracking-[-0.02em] md:text-5xl">
                What happens behind the scenes.
              </h2>
              <p className="mt-5 max-w-md text-sm leading-relaxed text-fog md:text-base">
                {project.ownerSideCopy ||
                  (isTemplate
                  ? "The tools behind each build are chosen for the job—whether that's handling inquiries, collecting estimate details, or managing day-to-day work."
                  : "Built around the work this business needs to get done, without adding unnecessary steps.")}
              </p>
              <div className="mt-8">
                <MediaSlot
                  label="Business owner view"
                  src={project.media.backend}
                  ratio="aspect-[16/10]"
                  testId="media-backend"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ── capabilities ─────────────────────────────────── */}
        <section className="mt-24 md:mt-36">
          <Reveal>
            <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
              Inside the build
            </p>
          </Reveal>
          <ul className="mt-6" data-testid="project-capabilities">
            {project.capabilities.map((cap, i) => (
              <Reveal key={cap} delay={0.04 * i}>
                <li className="flex items-baseline gap-6 border-t border-white/10 py-5 last:border-b">
                  <span className="text-xs tracking-[0.3em] text-fog/60">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-xl font-medium tracking-[-0.02em] md:text-3xl">
                    {cap}
                  </span>
                </li>
              </Reveal>
            ))}
          </ul>
        </section>

        {/* ── make this yours ──────────────────────────────── */}
        {project.purchaseEligible && (
          <section className="mt-24 md:mt-36" id="make-yours" data-testid="make-yours">
            <Reveal>
              <p className="text-[10px] font-medium uppercase tracking-[0.5em] text-fog md:text-xs">
                Make this yours
              </p>
              <h2 className="mt-4 max-w-3xl font-display text-3xl font-medium leading-[1.02] tracking-[-0.02em] md:text-5xl">
                Like what you see? Make it yours.
              </h2>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-fog md:text-base">
                {project.makeYoursCopy ||
                  "Your name, your brand, your services, and your details—built on a working foundation and tailored to your business."}
              </p>
            </Reveal>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              <Reveal delay={0.05}>
                <div className="flex h-full flex-col justify-between border border-white/15 p-6 md:p-8" data-testid="make-yours-own">
                  <div>
                    <p className="font-display text-2xl font-medium tracking-[-0.02em]">Own it</p>
                    <p className="mt-3 text-sm leading-relaxed text-fog">
                      A version customized for your business, with a one-time purchase instead
                      of a monthly site plan.
                    </p>
                  </div>
                  <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-fog/50">
                    {project.priceOnce ? (
                      <span className="font-display text-2xl normal-case tracking-[-0.02em] text-white">
                        ${project.priceOnce.toLocaleString()}
                        <span className="ml-2 text-xs text-fog">one time</span>
                      </span>
                    ) : (
                      "Exact price per build — arrives with the demo"
                    )}
                  </p>
                  {project.priceOnce && (
                    <Link
                      to={`/purchase/${project.slug}?option=own`}
                      data-testid="make-yours-own-cta"
                      className="group mt-6 inline-flex w-fit items-center gap-2 border border-white px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-500 ease-cinematic hover:bg-white hover:text-obsidian"
                    >
                      Own it — ${project.priceOnce.toLocaleString()}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5" />
                    </Link>
                  )}
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="flex h-full flex-col justify-between border border-white/15 p-6 md:p-8" data-testid="make-yours-waas">
                  <div>
                    <p className="font-display text-2xl font-medium tracking-[-0.02em]">
                      Website as a Service
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-fog">
                      A customized website with a monthly plan instead of a one-time purchase.
                    </p>
                    <p className="mt-3 text-xs leading-relaxed text-fog/60">
                      Cancel with at least 30 days' notice before your next billing date.{" "}
                      <Link to="/manage" className="underline underline-offset-4 transition-colors hover:text-white">
                        Subscription terms
                      </Link>
                    </p>
                  </div>
                  <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-fog/50">
                    {project.priceMonthly ? (
                      <span className="font-display text-2xl normal-case tracking-[-0.02em] text-white">
                        ${project.priceMonthly.toLocaleString()}
                        <span className="ml-2 text-xs text-fog">/ month</span>
                      </span>
                    ) : (
                      "Monthly per build — arrives with the demo"
                    )}
                  </p>
                  {project.priceMonthly && (
                    <Link
                      to={`/purchase/${project.slug}?option=waas`}
                      data-testid="make-yours-waas-cta"
                      className="group mt-6 inline-flex w-fit items-center gap-2 border border-white/40 px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-500 ease-cinematic hover:border-white hover:bg-white hover:text-obsidian"
                    >
                      Monthly — ${project.priceMonthly.toLocaleString()}/mo
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-500 group-hover:translate-x-0.5" />
                    </Link>
                  )}
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link
                  to="/start"
                  data-testid="make-yours-start-link"
                  className="group flex items-center gap-2 border border-white bg-white px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-obsidian transition-colors duration-500 ease-cinematic hover:bg-transparent hover:text-white"
                >
                  Start the Conversation
                  <ArrowRight className="h-4 w-4 transition-transform duration-500 ease-cinematic group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/start"
                  data-testid="build-custom-link"
                  className="text-xs uppercase tracking-[0.3em] text-fog transition-colors duration-300 hover:text-white"
                >
                  Or build something custom
                </Link>
              </div>
            </Reveal>
          </section>
        )}

        {/* ── next project ─────────────────────────────────── */}
        {next && (
          <Link
            to={`/work/${next.slug}`}
            data-testid="next-project-link"
            className="group mt-24 flex items-end justify-between gap-8 border-t border-white/10 pt-10 md:mt-36"
          >
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-fog">Next in the showroom</p>
              <p className="mt-3 font-display text-3xl font-medium tracking-[-0.02em] transition-transform duration-700 ease-cinematic group-hover:translate-x-2 md:text-6xl">
                {next.name}
              </p>
            </div>
            <ArrowRight className="h-7 w-7 shrink-0 text-fog transition-all duration-500 ease-cinematic group-hover:translate-x-1 group-hover:text-white md:h-10 md:w-10" />
          </Link>
        )}
      </div>
    </div>
  );
}
